import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider, createTheme } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import Students from './pages/UserManagement/Students';
import AddStudent from './pages/UserManagement/AddStudent';
import Alumni from './pages/UserManagement/Alumni';
import StudentView from './pages/UserManagement/StudentView';
import AlumniView from './pages/UserManagement/AlumniView';
import Teachers from './pages/UserManagement/Teachers';
import TeacherView from './pages/UserManagement/TeacherView';
import Parents from './pages/UserManagement/Parents';
import ParentView from './pages/UserManagement/ParentView';
import Classes from './pages/ClassesAndSubjects/Classes';
import Subjects from './pages/ClassesAndSubjects/Subjects';
import ClassStudents from './pages/ClassesAndSubjects/ClassStudents';
import Exams from './pages/ExamsAndResults/Exams';
import Results from './pages/ExamsAndResults/Results';
import AddResults from './pages/ExamsAndResults/AddResults';
import Feedback from './pages/ExamsAndResults/Feedback';
import Attendance from './pages/Attendance';
import Announcements from './pages/Announcements';
import Setup from './pages/Setup/Setup';
import Promotion from './pages/Promotion';
import LeaveManagement from './pages/LeaveManagement/LeaveManagement';
import Settings from './pages/Settings';
import Routine from './pages/Routine';
import FeeCategoriesPage from './pages/FeeManagement/Categories';
import AssignClassFeesPage from './pages/FeeManagement/ClassFees';
import AssignStudentFeesPage from './pages/FeeManagement/StudentFees';
import FeeTransactionsPage from './pages/FeeManagement/Transactions';
import FeeStatusPage from './pages/FeeManagement/FeeStatus';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAppConfig } from './contexts/AppConfigContext';
import { useLocation } from 'react-router-dom';
import PWAInstallPrompt from './components/Common/PWAInstallPrompt';

// Teacher allowed routes
const TEACHER_ALLOWED_ROUTES = [
  '/teacher-dashboard',
  '/subjects',
  '/subjects/class',
  '/attendance',
  '/results',
  '/results/add',
  '/feedback',
  '/fees/status',
  '/leave',
  '/settings',
  '/fees/status'
];

// Component to protect teacher routes
function TeacherRouteGuard({ children }) {
  const { isTeacher, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user is a teacher (not admin)
    const roleName = user?.role_name || '';
    const isTeacherUser = roleName === 'Teacher';
    const isAdminUser = roleName === 'Admin';
    
    if (isTeacherUser && !isAdminUser) {
      // Check if current route is allowed for teachers
      const isAllowedRoute = TEACHER_ALLOWED_ROUTES.some(route => 
        location.pathname === route || location.pathname.startsWith(route + '/')
      );
      
      if (!isAllowedRoute && location.pathname !== '/login') {
        // Redirect to teacher dashboard
        navigate('/teacher-dashboard', { replace: true });
      }
    }
  }, [location.pathname, user, navigate]);
  
  return children;
}

// Generic component to protect routes based on feature config
function FeatureRouteGuard({ children, featureEnabled, loading: configLoading }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!configLoading && !featureEnabled) {
      // Redirect to dashboard if feature is disabled
      navigate('/dashboard', { replace: true });
    }
  }, [featureEnabled, configLoading, navigate]);

  // Show loading or nothing while checking
  if (configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only render children if feature is enabled
  if (!featureEnabled) {
    return null;
  }

  return children;
}

function AppContent() {
  const { isAuthenticated, loading, isParent, isTeacher } = useAuth();
  const { themeColor, feeManagementEnabled, promotionEnabled, loading: configLoading } = useAppConfig();

  // Create dynamic theme based on config
  const dynamicTheme = React.useMemo(() => {
    return createTheme({
      palette: {
        primary: {
          main: themeColor || '#115263',
          light: '#42a5f5',
          dark: '#1565c0',
          contrastText: '#fff',
        },
        secondary: {
          main: '#dc004e',
          light: '#ff5983',
          dark: '#9a0036',
          contrastText: '#fff',
        },
        background: {
          default: '#f5f5f5',
          paper: '#ffffff',
        },
        text: {
          primary: '#333333',
          secondary: '#666666',
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontSize: '2.5rem', fontWeight: 500 },
        h2: { fontSize: '2rem', fontWeight: 500 },
        h3: { fontSize: '1.75rem', fontWeight: 500 },
        h4: { fontSize: '1.5rem', fontWeight: 500 },
        h5: { fontSize: '1.25rem', fontWeight: 500 },
        h6: { fontSize: '1rem', fontWeight: 500 },
      },
      shape: { borderRadius: 8 },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderRadius: 12,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            },
          },
        },
      },
    });
  }, [themeColor]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect component for dashboard based on role
  const DashboardRedirect = () => {
    // Check if user is a parent - if so, log them out and redirect to login
    if (isParent()) {
      // Clear authentication and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      return <Navigate to="/login" replace />;
    }
    if (isTeacher()) {
      return <Navigate to="/teacher-dashboard" replace />;
    }
    return <Dashboard />;
  };

  // Redirect component for root path
  const RootRedirect = () => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    // Check if user is a parent - if so, log them out and redirect to login
    if (isParent()) {
      // Clear authentication and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      return <Navigate to="/login" replace />;
    }
    if (isTeacher()) {
      return <Navigate to="/teacher-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <PWAInstallPrompt />
      <TeacherRouteGuard>
        <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated
              ? isParent()
                ? (() => {
                    // Clear parent authentication and show login page
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userRole');
                    localStorage.removeItem('userEmail');
                    return <Login />;
                  })()
                : isTeacher()
                ? <Navigate to="/teacher-dashboard" />
                : <Navigate to="/dashboard" />
              : <Login />
          }
        />
        <Route
          path="/dashboard"
          element={isAuthenticated ? <DashboardRedirect /> : <Navigate to="/login" />}
        />
        <Route
          path="/teacher-dashboard"
          element={isAuthenticated ? <TeacherDashboard /> : <Navigate to="/login" />}
        />
        <Route path="/users/students" element={isAuthenticated ? <Students /> : <Navigate to="/login" />} />
        <Route path="/users/students/add" element={isAuthenticated ? <AddStudent /> : <Navigate to="/login" />} />
        <Route path="/users/students/edit/:id" element={isAuthenticated ? <AddStudent /> : <Navigate to="/login" />} />
        <Route path="/users/students/:id" element={isAuthenticated ? <StudentView /> : <Navigate to="/login" />} />
        <Route path="/users/alumni" element={isAuthenticated ? <Alumni /> : <Navigate to="/login" />} />
        <Route path="/users/alumni/:id" element={isAuthenticated ? <AlumniView /> : <Navigate to="/login" />} />
        <Route path="/users/teachers" element={isAuthenticated ? <Teachers /> : <Navigate to="/login" />} />
        <Route path="/users/teachers/:id" element={isAuthenticated ? <TeacherView /> : <Navigate to="/login" />} />
        <Route path="/users/parents" element={isAuthenticated ? <Parents /> : <Navigate to="/login" />} />
        <Route path="/users/parents/:id" element={isAuthenticated ? <ParentView /> : <Navigate to="/login" />} />
        <Route path="/classes" element={isAuthenticated ? <Classes /> : <Navigate to="/login" />} />
        <Route path="/subjects" element={isAuthenticated ? <Subjects /> : <Navigate to="/login" />} />
        <Route path="/subjects/class/:classId" element={isAuthenticated ? <ClassStudents /> : <Navigate to="/login" />} />
        <Route path="/exams" element={isAuthenticated ? <Exams /> : <Navigate to="/login" />} />
        <Route path="/results" element={isAuthenticated ? <Results /> : <Navigate to="/login" />} />
        <Route path="/results/add" element={isAuthenticated ? <AddResults /> : <Navigate to="/login" />} />
        <Route path="/feedback" element={isAuthenticated ? <Feedback /> : <Navigate to="/login" />} />
        <Route path="/attendance" element={isAuthenticated ? <Attendance /> : <Navigate to="/login" />} />
        <Route path="/announcements" element={isAuthenticated ? <Announcements /> : <Navigate to="/login" />} />
        <Route path="/notification-users" element={isAuthenticated ? <div>Notification Users Management</div> : <Navigate to="/login" />} />
        <Route path="/leave" element={isAuthenticated ? <LeaveManagement /> : <Navigate to="/login" />} />
        <Route path="/routine" element={isAuthenticated ? <Routine /> : <Navigate to="/login" />} />
        <Route 
          path="/fees/categories" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={feeManagementEnabled} loading={configLoading}>
                <FeeCategoriesPage />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/fees/classes" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={feeManagementEnabled} loading={configLoading}>
                <AssignClassFeesPage />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/fees/students" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={feeManagementEnabled} loading={configLoading}>
                <AssignStudentFeesPage />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/fees/transactions" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={feeManagementEnabled} loading={configLoading}>
                <FeeTransactionsPage />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route 
          path="/fees/status" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={feeManagementEnabled} loading={configLoading}>
                <FeeStatusPage />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route path="/setup" element={isAuthenticated ? <Setup /> : <Navigate to="/login" />} />
        <Route 
          path="/promotion" 
          element={
            isAuthenticated ? (
              <FeatureRouteGuard featureEnabled={promotionEnabled} loading={configLoading}>
                <Promotion />
              </FeatureRouteGuard>
            ) : (
              <Navigate to="/login" />
            )
          } 
        />
        <Route path="/settings" element={isAuthenticated ? <Settings /> : <Navigate to="/login" />} />
        <Route
          path="/"
          element={<RootRedirect />}
        />
      </Routes>
      </TeacherRouteGuard>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
      <ToastContainer />
    </AuthProvider>
  );
}

export default App;
